import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { toast } from "sonner";
import "./PgiDashboard.css";

const API = `${getBackendUrl()}/api`;

const DOMAIN_ORDER = ["outcome", "ect", "ifse", "sscp", "dl", "gp"];
const HEAT_LABELS = { outcome: "OUT", ect: "ECT", ifse: "INF", sscp: "SAF", dl: "DL", gp: "GOV" };

const radarPoint = (cx, cy, radius, index, total, valuePct) => {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  const r = (Math.max(0, Math.min(100, valuePct)) / 100) * radius;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
};

const RankList = ({ rows = [] }) => (
  <div className="rank-list">
    {rows.map((row) => (
      <div
        key={`${row.district}-${row.rank}`}
        className={`rank-row${row.highlight ? " pune-highlight" : ""}`}
      >
        <div className="rank-label">
          <b>{row.district}</b>
          <span>Rank {row.rank}</span>
        </div>
        <div className="rank-track">
          <i className={row.highlight ? "good" : row.bar_class || ""} style={{ width: `${row.bar_pct}%` }} />
        </div>
        <strong>{row.score ?? row.yoy ?? row.movement}</strong>
      </div>
    ))}
  </div>
);

const PgiDashboard = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "executive";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/pgi/overview`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Pune PGI dashboard data");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const gaugeStyle = useMemo(() => {
    const pct = data?.executive?.pgi_index_pct ?? 0;
    const color = pct >= 80 ? "var(--success)" : pct >= 50 ? "var(--warning)" : "var(--danger)";
    return {
      background: `radial-gradient(circle at center,#fff 0 52%,transparent 53%),conic-gradient(${color} 0 ${pct}%,rgba(245,158,11,.14) ${pct}% 100%)`,
    };
  }, [data?.executive?.pgi_index_pct]);

  const radarPolygon = useMemo(() => {
    const radar = data?.domains?.radar || [];
    if (!radar.length) return "";
    const cx = 130;
    const cy = 130;
    const radius = 90;
    return radar
      .map((d, i) => radarPoint(cx, cy, radius, i, radar.length, d.value).join(","))
      .join(" ");
  }, [data?.domains?.radar]);

  if (loading) {
    return (
      <div className="content">
        <p style={{ color: "var(--muted)" }}>Loading Pune PGI dashboard…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <p style={{ color: "var(--danger)" }}>Pune PGI data unavailable. Ensure PGI Data.xlsx is in data/excel.</p>
      </div>
    );
  }

  const exec = data.executive;
  const meta = data.meta;

  return (
    <div className="content">
      <section className="page-header">
        <div>
          <div className="eyebrow">Pune District · Performance Grading Index · Official Review Ready</div>
          <h1>Pune District PGI Dashboard</h1>
          <p>
            All KPIs, domain visuals, benchmarking, and action views are derived from Pune district rows in
            PGI Data.xlsx and District_PGI_2023-24 CSV. State rankings appear only as benchmark context.
          </p>
        </div>
        <button type="button" className="primary-action" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      <div className="top-actions" style={{ marginBottom: 8, justifyContent: "flex-end", display: "flex", gap: 10 }}>
        <span className="filter-chip">
          District <b>{meta.district || "Pune"}</b>
        </span>
        <span className="filter-chip">
          Year <b>{meta.year}</b>
        </span>
        <span className="filter-chip">
          Scope <b>{meta.scope || "Pune District"}</b>
        </span>
      </div>

      {activeTab === "executive" && (
        <section className="view active" id="executive">
          <div className="hero-card">
            <div className="hero-layout">
              <div>
                <div className="gauge-wrap">
                  <div className="gauge" style={gaugeStyle}>
                    <div>
                      <strong>{exec.pgi_index_pct}%</strong>
                      <span>PGI Index</span>
                    </div>
                  </div>
                  <div className="hero-copy">
                    <h2>{exec.headline}</h2>
                    <p>{exec.summary}</p>
                    <div className="chip-row">
                      {exec.chips?.map((chip) => (
                        <span key={chip} className={`status-chip${chip.includes("band") ? "" : " blue"}`}>
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="summary-note">
                  <div className="icon-badge">ACT</div>
                  <div>
                    <b>Primary executive action for Pune</b>
                    <span>{exec.primary_action}</span>
                  </div>
                </div>
              </div>
              <div className="kpi-grid">
                {exec.kpis?.map((kpi) => (
                  <article key={kpi.label} className={`kpi-card ${kpi.tone || ""}`.trim()}>
                    <small>{kpi.label}</small>
                    <strong>{kpi.value}</strong>
                    <span>{kpi.hint}</span>
                    <em>{kpi.note}</em>
                  </article>
                ))}
              </div>
            </div>
          </div>
          <div className="section-title">
            <div>
              <h2>Pune score snapshot</h2>
              <p>Current score, YoY movement, and position relative to the Maharashtra state average.</p>
            </div>
          </div>
          <div className="dist-grid">
            {exec.distribution?.map((band) => (
              <div key={band.label} className="dist-card">
                <small>{band.label}</small>
                <strong>{band.count}</strong>
                <span>{band.note || "Pune"}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === "ranking" && (
        <section className="view active" id="ranking">
          <div className="visual-card" style={{ marginBottom: 20 }}>
            <div className="grid-3">
              <article className="kpi-card blue">
                <small>Pune State Rank</small>
                <strong>{data.ranking?.pune_rank}</strong>
                <span>Out of {data.ranking?.total_districts} districts</span>
              </article>
              <article className="kpi-card green">
                <small>Pune PGI Score</small>
                <strong>{data.ranking?.pune_score}</strong>
                <span>2024–25 overall score</span>
              </article>
              <article className="kpi-card purple">
                <small>vs State Average</small>
                <strong>
                  {data.ranking?.vs_state >= 0 ? "+" : ""}
                  {data.ranking?.vs_state}
                </strong>
                <span>State average {data.ranking?.state_avg}</span>
              </article>
            </div>
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Top 10 districts — Pune in context</h3>
                  <p>Maharashtra leaders with Pune highlighted at rank {data.ranking?.pune_rank}.</p>
                </div>
                <span className="pill">Ranking</span>
              </div>
              <RankList rows={data.ranking?.top10} />
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune performance position</h3>
                  <p>How Pune compares to the state average and the leading district.</p>
                </div>
                <span className="pill">Pune</span>
              </div>
              <div className="grid-3">
                <article className="kpi-card green">
                  <small>Above State Average</small>
                  <strong>{data.ranking?.above_avg ? "Yes" : "No"}</strong>
                  <span>Pune score {data.ranking?.pune_score}</span>
                </article>
                <article className="kpi-card amber">
                  <small>Gap to Leader</small>
                  <strong>{data.ranking?.score_range}</strong>
                  <span>Points behind rank 1</span>
                </article>
                <article className="kpi-card blue">
                  <small>Peer Band</small>
                  <strong>Top 10</strong>
                  <span>Pune in upper performance tier</span>
                </article>
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "domains" && (
        <section className="view active" id="domains">
          <div className="domain-grid">
            {data.domains?.cards?.map((d) => (
              <article key={d.id} className="domain-card">
                <div className="domain-top">
                  <div className="icon-badge">{d.short}</div>
                  <span className={`status-dot ${d.status_dot}`} />
                </div>
                <h3>{d.label}</h3>
                <div className="domain-score">
                  <strong>{d.score}</strong>
                  <span>/ {d.max}</span>
                </div>
                <div className="progress">
                  <i style={{ width: `${d.achievement_pct}%` }} />
                </div>
                <div className="mini-grid">
                  <span>
                    Achievement <b>{d.achievement_pct}%</b>
                  </span>
                  <span>
                    YoY <b>{d.diff > 0 ? "+" : ""}{d.diff}</b>
                  </span>
                  <span>
                    vs State <b>{d.vs_state >= 0 ? "+" : ""}{d.vs_state}</b>
                  </span>
                  <span>
                    Best <b>{d.best_district}</b>
                  </span>
                </div>
              </article>
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune domain radar</h3>
                  <p>Achievement percentage across six PGI domains for Pune district.</p>
                </div>
                <span className="pill">{meta.year}</span>
              </div>
              <svg viewBox="0 0 260 260" className="radar">
                {[0.25, 0.5, 0.75, 1].map((scale) => (
                  <polygon
                    key={scale}
                    points={data.domains.radar
                      .map((_, i) => radarPoint(130, 130, 90 * scale, i, data.domains.radar.length, 100).join(","))
                      .join(" ")}
                    fill="none"
                    stroke="#d7e3f6"
                    strokeWidth="1"
                  />
                ))}
                <polygon points={radarPolygon} fill="rgba(37,99,235,.30)" stroke="#2563eb" strokeWidth="3" />
                {data.domains.radar.map((d, i) => {
                  const [x, y] = radarPoint(130, 130, 98, i, data.domains.radar.length, 100);
                  return (
                    <text key={d.label} x={x} y={y} textAnchor="middle">
                      {d.label}
                    </text>
                  );
                })}
              </svg>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune 2023–24 vs 2024–25 domains</h3>
                  <p>Blue shows current year, orange shows previous year.</p>
                </div>
                <span className="pill">YoY</span>
              </div>
              <div className="compare-list">
                {data.domains?.compare_yoy?.map((row) => (
                  <div key={row.domain} className="compare-row">
                    <span>{row.domain}</span>
                    <div className="dual">
                      <i style={{ width: `${row.py_pct}%` }} />
                      <b style={{ width: `${row.cy_pct}%` }} />
                    </div>
                    <strong>{row.cy_pct}%</strong>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "yoy" && (
        <section className="view active" id="yoy">
          <div className="visual-card" style={{ marginBottom: 20 }}>
            <div className="grid-3">
              <article className="kpi-card green">
                <small>Pune YoY Gain</small>
                <strong>+{data.yoy?.pune_yoy}</strong>
                <span>Overall score improvement</span>
              </article>
              <article className="kpi-card blue">
                <small>YoY Rank (State)</small>
                <strong>{data.yoy?.pune_yoy_rank}</strong>
                <span>Among all Maharashtra districts</span>
              </article>
              <article className="kpi-card purple">
                <small>Rank Movement</small>
                <strong>+{data.yoy?.rank_movement}</strong>
                <span>Positions gained vs 2023–24</span>
              </article>
            </div>
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>State YoY leaders — Pune highlighted</h3>
                  <p>Largest score increases from 2023–24 to 2024–25.</p>
                </div>
                <span className="pill">Score change</span>
              </div>
              <RankList rows={data.yoy?.top_improvers?.map((r) => ({ ...r, score: r.yoy, bar_class: r.highlight ? "good" : "good" }))} />
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune rank movement</h3>
                  <p>Pune improved from rank {data.deepdive?.rank + (data.yoy?.rank_movement || 0)} to rank {data.deepdive?.rank}.</p>
                </div>
                <span className="pill">Pune</span>
              </div>
              <RankList rows={data.yoy?.rank_gainers?.map((r) => ({ ...r, score: r.movement }))} />
            </article>
          </div>
          <div className="visual-card">
            <div className="section-title">
              <div>
                <h2>Pune domain movement waterfall</h2>
                <p>Year-on-year change by domain for Pune district.</p>
              </div>
            </div>
            <div className="waterfall">
              {data.yoy?.waterfall?.map((row) => (
                <div key={row.domain} className="water-row">
                  <span>{row.domain}</span>
                  <div className="waterbar">
                    <i className={row.negative ? "negative" : ""} style={{ width: `${row.bar_pct}%` }} />
                  </div>
                  <strong>
                    {row.diff > 0 ? "+" : ""}
                    {row.diff}
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === "benchmark" && (
        <section className="view active" id="benchmark">
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune domain achievement</h3>
                  <p>Current domain scores as percentage of maximum marks.</p>
                </div>
                <span className="pill">% of max</span>
              </div>
              <div className="compare-list">
                {data.benchmark?.domain_benchmark?.map((row) => (
                  <div key={row.domain} className="compare-row">
                    <span>{row.domain}</span>
                    <div className="dual">
                      <b style={{ width: `${row.pct}%` }} />
                    </div>
                    <strong>{row.pct}%</strong>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune vs state average by domain</h3>
                  <p>Blue is Pune; comparison against Maharashtra state average.</p>
                </div>
                <span className="pill">Benchmark</span>
              </div>
              <div className="compare-list">
                {data.benchmark?.state_compare?.map((row) => (
                  <div key={row.domain} className="compare-row">
                    <span>{row.domain}</span>
                    <div className="dual">
                      <i style={{ width: `${row.state_pct}%` }} />
                      <b style={{ width: `${row.pune_pct}%` }} />
                    </div>
                    <strong>{row.pune_pct}%</strong>
                  </div>
                ))}
              </div>
            </article>
          </div>
          <article className="panel" style={{ marginTop: 20 }}>
            <div className="panel-head">
              <div>
                <h3>Pune gap to best district by domain</h3>
                <p>Distance from Pune to the top-performing Maharashtra district in each domain.</p>
              </div>
              <span className="pill">Gap</span>
            </div>
            <div className="rank-list">
              {data.benchmark?.domain_gaps?.map((row) => (
                <div key={row.domain} className="rank-row">
                  <div className="rank-label">
                    <b>{row.domain}</b>
                    <span>
                      Pune {row.pune_score} vs {row.best_district} {row.best_score}
                    </span>
                  </div>
                  <div className="rank-track">
                    <i className="warn" style={{ width: `${row.bar_pct}%` }} />
                  </div>
                  <strong>{row.gap}</strong>
                </div>
              ))}
            </div>
          </article>
          <article className="panel" style={{ marginTop: 20 }}>
            <div className="panel-head">
              <div>
                <h3>Pune domain heatmap</h3>
                <p>Single-row view of Pune achievement across all six PGI domains.</p>
              </div>
              <span className="pill">Pune × 6</span>
            </div>
            <div className="heatmap">
              <div className="heat-head">
                <b>District</b>
                {DOMAIN_ORDER.map((k) => (
                  <span key={k}>{HEAT_LABELS[k]}</span>
                ))}
                <strong>PGI</strong>
              </div>
              {data.benchmark?.heatmap?.map((row) => (
                <div key={row.district} className="heat-row pune-highlight">
                  <b>{row.district}</b>
                  {DOMAIN_ORDER.map((k) => (
                    <span key={k} className={`heat-cell ${row.cells[k]?.class || ""}`} title={`${HEAT_LABELS[k]} ${row.cells[k]?.pct}%`}>
                      {row.cells[k]?.display}
                    </span>
                  ))}
                  <strong>{row.pgi_score}</strong>
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      {activeTab === "risk" && (
        <section className="view active" id="risk">
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune risk position</h3>
                  <p>Pune plotted by score gap, domain weakness, and growth momentum.</p>
                </div>
                <span className="pill">Priority</span>
              </div>
              <div className="matrix">
                <span className="quad q1">High gap · high growth</span>
                <span className="quad q2">High risk · urgent</span>
                <span className="quad q3">Low risk · monitor</span>
                <span className="quad q4">Stable but weak</span>
                {data.risk?.bubbles?.map((b, idx) => (
                  <span key={`${b.label}-${idx}`} className="bubble" style={{ left: `${b.left_pct}%`, top: `${b.top_pct}%` }}>
                    {b.label}
                  </span>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Pune risk KPI cards</h3>
                  <p>Intervention indicators for Pune district leadership.</p>
                </div>
                <span className="pill">Action</span>
              </div>
              <div className="grid-3">
                <article className="kpi-card green">
                  <small>Priority Status</small>
                  <strong>{data.risk?.priority_status || data.deepdive?.priority_status}</strong>
                  <span>Index {data.risk?.priority_index}</span>
                </article>
                <article className="kpi-card amber">
                  <small>Focus Domains</small>
                  <strong>{data.risk?.focus_domain_count}</strong>
                  <span>Below 60% achievement</span>
                </article>
                <article className="kpi-card purple">
                  <small>Flat Domains</small>
                  <strong>{data.risk?.flat_domain_count}</strong>
                  <span>No YoY improvement</span>
                </article>
              </div>
            </article>
          </div>
          <div className="risk-grid">
            {data.risk?.cards?.map((card) => (
              <article key={card.district} className="risk-card">
                <div>
                  <small>Priority index</small>
                  <strong>{card.priority_index}</strong>
                </div>
                <h4>{card.district}</h4>
                <p>
                  Score {card.score}, weak domains {card.weak_domains}, YoY +{card.yoy}.
                </p>
                <span>Status: {data.risk?.priority_status}</span>
              </article>
            ))}
          </div>
          {data.risk?.actions?.length > 0 && (
            <div className="visual-card" style={{ marginTop: 20 }}>
              <div className="section-title">
                <div>
                  <h2>Recommended actions for Pune</h2>
                  <p>Derived from domain gaps, flat performance, and benchmark leaders.</p>
                </div>
              </div>
              <div className="grid-3" style={{ marginTop: 22 }}>
                {data.risk.actions.map((action, idx) => (
                  <article key={action} className={`kpi-card ${idx === 0 ? "amber" : idx === 1 ? "blue" : "green"}`}>
                    <small>Action {idx + 1}</small>
                    <strong>{action.split("—")[0].trim()}</strong>
                    <span>{action.includes("—") ? action.split("—").slice(1).join("—").trim() : action}</span>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {activeTab === "deepdive" && (
        <section className="view active" id="deepdive">
          <div className="visual-card deep-card">
            <div className="district-profile">
              <h3>{data.deepdive?.district}</h3>
              <span>Pune district profile</span>
              <strong>{data.deepdive?.score}</strong>
              <span>
                Rank {data.deepdive?.rank} · +{data.deepdive?.yoy} YoY
              </span>
            </div>
            <div>
              <div className="section-title">
                <div>
                  <h2>Pune deep-dive visual</h2>
                  <p>Score, rank, improvement, and domain performance for Pune district.</p>
                </div>
              </div>
              <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginTop: 22 }}>
                <article className="kpi-card green">
                  <small>District Score</small>
                  <strong>{data.deepdive?.score}</strong>
                  <span>Current year score</span>
                  <em>Rank {data.deepdive?.rank}</em>
                </article>
                <article className="kpi-card green">
                  <small>Improvement %</small>
                  <strong>+{data.deepdive?.improvement_pp} pp</strong>
                  <span>YoY percentage point change</span>
                  <em>Positive</em>
                </article>
                <article className="kpi-card blue">
                  <small>Best Domain</small>
                  <strong>{data.deepdive?.best_domain}</strong>
                  <span>Strongest achievement area</span>
                  <em>Lead strength</em>
                </article>
                <article className="kpi-card purple">
                  <small>Priority Status</small>
                  <strong>{data.deepdive?.priority_status}</strong>
                  <span>Focus: {data.deepdive?.weakest_domain}</span>
                  <em>Benchmark</em>
                </article>
              </div>
              <div className="compare-list" style={{ marginTop: 24 }}>
                {data.deepdive?.domains?.map((row) => (
                  <div key={row.label} className="compare-row">
                    <span>{row.domain}</span>
                    <div className="dual">
                      <b style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <strong>{row.score}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === "officials" && (
        <section className="view active" id="officials">
          <div className="grid-3">
            <article className="persona">
              <div className="icon-badge">ES</div>
              <h3>Education Secretary View</h3>
              <p>Pune performance in state context — rank, YoY trajectory, and domain strengths.</p>
              <div className="persona-metrics">
                <span>
                  Pune Score <b>{data.officials?.district_score}</b>
                </span>
                <span>
                  State Avg <b>{data.officials?.state_avg}</b>
                </span>
                <span>
                  Rank <b>{data.officials?.rank}</b>
                </span>
              </div>
            </article>
            <article className="persona">
              <div className="icon-badge">COM</div>
              <h3>Commissioner View</h3>
              <p>Domain benchmarking, intervention priorities, and monitoring cadence for Pune.</p>
              <div className="persona-metrics">
                <span>
                  YoY <b>+{data.officials?.yoy}</b>
                </span>
                <span>
                  Best <b>{data.officials?.best_domain}</b>
                </span>
                <span>
                  Focus <b>{data.officials?.weakest_domain}</b>
                </span>
              </div>
            </article>
            <article className="persona">
              <div className="icon-badge">CEO</div>
              <h3>Collector / CEO ZP View</h3>
              <p>Pune district action tracker, domain gap closure, and improvement roadmap.</p>
              <div className="persona-metrics">
                <span>
                  Priority <b>{data.officials?.priority_status}</b>
                </span>
                <span>
                  Gap to #1 <b>{data.officials?.gap_to_leader}</b>
                </span>
                <span>
                  Review <b>Monthly</b>
                </span>
              </div>
            </article>
          </div>
          <div className="visual-card">
            <div className="section-title">
              <div>
                <h2>Pune official review storyline</h2>
                <p>Monthly review flow for Pune district: diagnose, prioritise, and act.</p>
              </div>
            </div>
            <div className="grid-3" style={{ marginTop: 22 }}>
              <article className="kpi-card blue">
                <small>1. Diagnose</small>
                <strong>Score {data.officials?.district_score}</strong>
                <span>Rank {data.officials?.rank} · domain heatmap</span>
              </article>
              <article className="kpi-card amber">
                <small>2. Prioritise</small>
                <strong>{data.officials?.weakest_domain}</strong>
                <span>Weakest domain · {data.officials?.weak_domains} below threshold</span>
              </article>
              <article className="kpi-card green">
                <small>3. Act</small>
                <strong>+{data.officials?.yoy} YoY</strong>
                <span>Sustain momentum · close leader gap</span>
              </article>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PgiDashboard;
